import { type ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import DataLayer from '~/services/DataLayer';
import toast from '~/utils/toast';

import ActionForm from '../ActionForm';
import { useComponentFetcher } from '../ComponentErrorBoundary/useComponentFetcher';
import RedirectToHiddenInput from '../Forms/RedirectToHiddenInput';
import StatusInputs from '../Forms/StatusInputs';

type RenderProps = {
  isLoading: boolean;
};

type Props = {
  email: string;
  publicationId: string;
  redirectTo: string;
  children: (props: RenderProps) => ReactNode;
  onSuccess?: () => void;
};

const RequestAccessAction = ({ email, publicationId, redirectTo, children, onSuccess }: Props) => {
  const fetcher = useComponentFetcher();
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);

  const handleSuccess = (data: any) => {
    if (data?.dataLayerEvent) {
      DataLayer.pushEvent(data.dataLayerEvent);
    }

    if (data?.toast?.message) {
      toast.success(data.toast.message);
    }

    formRef.current?.reset();

    onSuccess?.();
  };

  const isLoading = fetcher.state === 'submitting';

  const ssoRedirectRoute = '/authenticate/sso_redirect';

  return (
    <ActionForm
      fetcher={fetcher}
      formRef={formRef}
      method="post"
      action={ssoRedirectRoute}
      onSuccess={(data) => {
        handleSuccess(data);
      }}
      onError={(data) => {
        toast.error(data?.toast?.message || 'Something went wrong, please try again');
      }}
    >
      <input type="hidden" name="email" defaultValue={email} />
      <input type="hidden" name="publication_id" defaultValue={publicationId} />
      <RedirectToHiddenInput value={redirectTo} />
      <StatusInputs
        name="login"
        errorMessage={`${t('errors.default')} ${t('errors.try_later')}`}
        successMessage={t('login.success.message')}
      />
      {children({
        isLoading,
      })}
    </ActionForm>
  );
};

export default RequestAccessAction;
